import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Chip,
} from '@mui/material';
import {
  PhotoCamera,
  Edit,
  Save,
  Cancel,
  Lock,
  Pinterest,
  DeleteForever,
  Warning,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth, storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { stripeApi } from '../services/stripeApiService';
import { Capacitor } from '@capacitor/core';
import { iapService } from '../services/iapService';
import { redeemCoupon } from '../services/subscriptionService';
import { TopBar } from '../layout/TopBar';
import { appleOfferCode } from '../services/appleOfferCode';

export const SettingsPage: React.FC = () => {
  const { theme } = useTheme();
  const { user, hostlyUser, updateUserProfile, deleteAccount } = useAuth();
  const { subscription, isPremium, isPro, isFree, refreshSubscription } = useSubscription();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [restoreLoading, setRestoreLoading] = useState(false);

  // Coupon State
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponMessage, setCouponMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'success'>('error');

  const [formData, setFormData] = useState<{
    firstName: string;
    lastName: string;
    nickname: string;
    phone: string;
    cep: string;
    address: string;
    number: string;
    complement: string;
    neighborhood: string;
    city: string;
    state: string;
    photoURL: string;
    pinterestBoardUrl: string;
  }>({
    firstName: hostlyUser?.name || '',
    lastName: hostlyUser?.lastName || '',
    nickname: hostlyUser?.nickname || '',
    phone: hostlyUser?.phone || '',
    cep: (hostlyUser as any)?.cep || '',
    address: hostlyUser?.address || '',
    number: (hostlyUser as any)?.number || '',
    complement: (hostlyUser as any)?.complement || '',
    neighborhood: (hostlyUser as any)?.neighborhood || '',
    city: (hostlyUser as any)?.city || '',
    state: (hostlyUser as any)?.state || '',
    photoURL: hostlyUser?.profilePictureUrl || '',
    pinterestBoardUrl: (hostlyUser as any)?.pinterestBoardUrl || '',
  });

  const [error, setError] = useState('');
  const [loadingCep, setLoadingCep] = useState(false);

  useEffect(() => {
    if (hostlyUser) {
      setFormData({
        firstName: hostlyUser.name || '',
        lastName: hostlyUser.lastName || '',
        nickname: hostlyUser.nickname || '',
        phone: hostlyUser.phone || '',
        cep: (hostlyUser as any).cep || '',
        address: hostlyUser.address || '',
        number: (hostlyUser as any).number || '',
        complement: (hostlyUser as any).complement || '',
        neighborhood: (hostlyUser as any).neighborhood || '',
        city: (hostlyUser as any).city || '',
        state: (hostlyUser as any).state || '',
        photoURL: hostlyUser.profilePictureUrl || '',
        pinterestBoardUrl: (hostlyUser as any).pinterestBoardUrl || '',
      });
    }
  }, [hostlyUser]);

  const showSnackbar = (message: string, severity: 'error' | 'success' = 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleCepChange = async (cep: string) => {
    // Remove tudo que não for número
    const cleanCep = cep.replace(/\D/g, '');

    // Atualiza o campo CEP
    setFormData(prev => ({ ...prev, cep: cleanCep }));
    setError('');

    // Se o CEP tiver 8 dígitos, busca na API
    if (cleanCep.length === 8) {
      setLoadingCep(true);
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await response.json();

        if (data.erro) {
          showSnackbar('CEP não encontrado', 'error');
          return;
        }

        // Preenche os campos automaticamente
        setFormData(prev => ({
          ...prev,
          address: data.logradouro || '',
          neighborhood: data.bairro || '',
          city: data.localidade || '',
          state: data.uf || '',
        }));

        showSnackbar('Endereço preenchido automaticamente!', 'success');
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
        showSnackbar('Erro ao buscar CEP', 'error');
      } finally {
        setLoadingCep(false);
      }
    }
  };

  const handleSave = async () => {
    if (!user?.uid) {
      setError('Usuário não autenticado. Faça login novamente.');
      showSnackbar('Usuário não autenticado', 'error');
      return;
    }

    if (!formData.firstName || !formData.lastName) {
      setError('Nome e sobrenome são obrigatórios');
      return;
    }

    const phoneRegex = /^(\(?\d{2}\)?\s?)?\d{4,5}-?\d{4}$/;
    if (formData.phone && !phoneRegex.test(formData.phone.trim())) {
      setError('Formato de telefone inválido. Use: (81) 99567-3334 ou 81995673334');
      return;
    }

    setLoading(true);
    try {
      // Cria o endereço completo para salvar
      const fullAddress = `${formData.address}${formData.number ? ', ' + formData.number : ''}${formData.complement ? ', ' + formData.complement : ''} - ${formData.neighborhood} - ${formData.city}/${formData.state}${formData.cep ? ' - CEP: ' + formData.cep : ''}`;

      await updateUserProfile({
        name: formData.firstName,
        lastName: formData.lastName,
        nickname: formData.nickname.replace(/[^a-zA-Z0-9._]/g, '').toLowerCase(),
        phone: formData.phone,
        address: fullAddress,
        profilePictureUrl: formData.photoURL,
        cep: formData.cep,
        number: formData.number,
        complement: formData.complement,
        neighborhood: formData.neighborhood,
        city: formData.city,
        state: formData.state,
        pinterestBoardUrl: formData.pinterestBoardUrl,
      } as any);

      setEditing(false);
      showSnackbar('Perfil atualizado com sucesso!', 'success');
    } catch (error: any) {
      setError('Erro ao atualizar perfil. Tente novamente.');
      showSnackbar('Erro ao atualizar perfil', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: hostlyUser?.name || '',
      lastName: hostlyUser?.lastName || '',
      nickname: hostlyUser?.nickname || '',
      phone: hostlyUser?.phone || '',
      cep: (hostlyUser as any)?.cep || '',
      address: hostlyUser?.address || '',
      number: (hostlyUser as any)?.number || '',
      complement: (hostlyUser as any)?.complement || '',
      neighborhood: (hostlyUser as any)?.neighborhood || '',
      city: (hostlyUser as any)?.city || '',
      state: (hostlyUser as any)?.state || '',
      photoURL: hostlyUser?.profilePictureUrl || '',
      pinterestBoardUrl: (hostlyUser as any)?.pinterestBoardUrl || '',
    });
    setEditing(false);
    setError('');
  };

  const handleResetPassword = async () => {
    if (!user?.email) {
      showSnackbar('Email não encontrado', 'error');
      return;
    }

    setResetPasswordLoading(true);
    try {
      await sendPasswordResetEmail(auth, user.email);
      setResetPasswordDialog(false);
      showSnackbar('Email de redefinição enviado com sucesso!', 'success');
    } catch (error: any) {
      showSnackbar('Erro ao enviar email de redefinição', 'error');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.uid) {
      showSnackbar('Erro ao selecionar arquivo', 'error');
      return;
    }

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      showSnackbar('Por favor, selecione uma imagem', 'error');
      return;
    }

    // Validar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('A imagem deve ter no máximo 5MB', 'error');
      return;
    }

    setUploadingPhoto(true);
    try {
      // Upload para Firebase Storage
      const timestamp = Date.now();
      const storageRef = ref(storage, `users/${user.uid}/profile_${timestamp}.jpg`);
      await uploadBytes(storageRef, file);

      // Obter URL da imagem
      const photoURL = await getDownloadURL(storageRef);

      // Atualizar perfil
      await updateUserProfile({
        profilePictureUrl: photoURL,
      });

      // Atualizar estado local
      setFormData(prev => ({ ...prev, photoURL }));

      showSnackbar('Foto atualizada com sucesso!', 'success');
    } catch (error: any) {
      showSnackbar('Erro ao fazer upload da foto', 'error');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const getInitials = () => {
    const firstName = formData.firstName || hostlyUser?.name || '';
    const lastName = formData.lastName || hostlyUser?.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const handleManageSubscription = async () => {
    if (Capacitor.getPlatform() === 'ios') {
      showSnackbar('Gerencie sua assinatura nas configurações do iPhone (Ajustes > Seu Nome > Assinaturas)', 'error');
      return;
    }

    if (!subscription?.stripeCustomerId) {
      showSnackbar('Você ainda não tem uma assinatura ativa', 'error');
      return;
    }

    setPortalLoading(true);
    try {
      const result = await stripeApi.createPortalSession({
        customer: subscription.stripeCustomerId,
        return_url: window.location.origin + '/settings'
      });

      if (result.success && result.data?.session?.url) {
        window.location.href = result.data.session.url;
      } else {
        showSnackbar(result.error || 'Erro ao abrir portal de assinatura', 'error');
      }
    } catch (error) {
      showSnackbar('Erro ao abrir portal de assinatura', 'error');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleRestorePurchases = async () => {
    if (Capacitor.getPlatform() !== 'ios') {
      return;
    }

    setRestoreLoading(true);
    try {
      const result = await iapService.restorePurchases();

      if (result.transactions && result.transactions.length > 0) {
        await refreshSubscription();
        showSnackbar('Compras restauradas com sucesso!', 'success');
      } else {
        showSnackbar('Nenhuma compra encontrada para restaurar', 'error');
      }
    } catch (error: any) {
      showSnackbar('Erro ao restaurar compras', 'error');
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (Capacitor.getPlatform() === 'ios') {
      showSnackbar('Cancele sua assinatura nas configurações do iPhone (Ajustes > Seu Nome > Assinaturas)', 'error');
      setCancelDialogOpen(false);
      return;
    }

    if (!subscription?.stripeSubscriptionId) {
      showSnackbar('Nenhuma assinatura ativa para cancelar', 'error');
      return;
    }

    setCancelLoading(true);
    try {
      const result = await stripeApi.cancelSubscription(subscription.stripeSubscriptionId);

      if (result.success) {
        setCancelDialogOpen(false);
        showSnackbar('Assinatura cancelada. Você terá acesso até o fim do período pago.', 'success');
        setTimeout(() => window.location.reload(), 2000);
      } else {
        showSnackbar(result.error || 'Erro ao cancelar assinatura', 'error');
      }
    } catch (error) {
      showSnackbar('Erro ao cancelar assinatura', 'error');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText.toLowerCase() !== 'deletar') {
      showSnackbar('Digite "DELETAR" para confirmar', 'error');
      return;
    }

    setDeleteLoading(true);
    try {
      await deleteAccount();
      // Se chegou aqui, a conta foi deletada com sucesso
      // O usuário será deslogado automaticamente
      navigate('/');
    } catch (error: any) {
      console.error('Erro ao deletar conta:', error);
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
      showSnackbar(error.message || 'Erro ao deletar conta. Tente novamente.', 'error');
    }
  };

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) return;
    if (!user) return;

    setCouponLoading(true);
    setCouponMessage(null);

    try {
      const result = await redeemCoupon(user.uid, couponCode);
      if (result.success) {
        setCouponMessage({ type: 'success', text: result.message });
        await refreshSubscription();
        setTimeout(() => {
          setCouponDialogOpen(false);
          setCouponCode('');
          setCouponMessage(null);
          navigate('/checkout/success?session_id=internal_coupon');
        }, 1500);
        return;
      }

      if (appleOfferCode.isSupported()) {
        try {
          await appleOfferCode.present();
          setCouponDialogOpen(false);
          setCouponCode('');
          setCouponMessage(null);
          return;
        } catch {
          // se falhar, mantém a mensagem do redeemCoupon
        }
      }

      const stripeCode = await stripeApi.findActivePromotionCodeByName(couponCode.trim().toUpperCase());
      if (stripeCode) {
        setCouponMessage({ type: 'success', text: `Cupom ${stripeCode.code} válido. Redirecionando...` });
        setTimeout(() => {
          setCouponDialogOpen(false);
          setCouponCode('');
          setCouponMessage(null);
          navigate(`/pricing?coupon=${encodeURIComponent(stripeCode.code)}`);
        }, 1200);
        return;
      }

      setCouponMessage({ type: 'error', text: result.message });
    } catch {
      setCouponMessage({ type: 'error', text: 'Erro ao processar cupom.' });
    } finally {
      setCouponLoading(false);
    }
  };

  const getPlanLabel = () => {
    if (subscription?.status === 'trialing') {
      // Show the actual plan they're trialing
      if (subscription.plan === 'pro') return 'Trial Pro';
      if (subscription.plan === 'premium') return 'Trial Basic';
      return 'Trial';
    }
    if (isPro) return 'Pro';
    if (isPremium) return 'Basic';
    return 'Free';
  };

  const getPlanColor = () => {
    if (subscription?.status === 'trialing') return '#8B5CF6';
    if (isPro) return '#F59E0B';
    if (isPremium) return theme.colors.secondary;
    return theme.colors.text.secondary;
  };

  const formatDate = (date?: Date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  return (
    <>
      <TopBar
        title="Configurações"
        showBackButton={true}
        showProfileButton={false}
        onBackClick={() => navigate('/dashboard')}
      />

      <Box sx={{
        paddingBottom: '120px',
        padding: 2,
        maxWidth: '400px',
        margin: '0 auto',
        paddingTop: '70px',
      }}>

        {/* Profile Photo */}
        <Card sx={{
          marginBottom: 3,
          borderRadius: theme.radii.medium,
          backgroundColor: theme.colors.card,
          border: 'none',
          boxShadow: 'none',
        }}>
          <CardContent sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: 3,
            '&:last-child': { paddingBottom: 3 }
          }}>
            <Box sx={{ position: 'relative', mb: 2 }}>
              <Avatar
                src={formData.photoURL}
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: theme.colors.secondary,
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                }}
              >
                {!formData.photoURL && getInitials()}
              </Avatar>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="photo-upload-input"
                type="file"
                onChange={handlePhotoUpload}
              />
              <label htmlFor="photo-upload-input">
                <IconButton
                  component="span"
                  disabled={uploadingPhoto}
                  sx={{
                    position: 'absolute',
                    bottom: -4,
                    right: -4,
                    backgroundColor: theme.colors.primary,
                    color: 'white',
                    width: 32,
                    height: 32,
                    '&:hover': {
                      backgroundColor: theme.colors.primary,
                      opacity: 0.8,
                    },
                    '&.Mui-disabled': {
                      backgroundColor: theme.colors.primary,
                      opacity: 0.5,
                    },
                  }}
                >
                  {uploadingPhoto ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PhotoCamera fontSize="small" />
                  )}
                </IconButton>
              </label>
            </Box>
            <Typography
              sx={{
                color: theme.colors.text.primary,
                fontWeight: 'bold',
                fontSize: '1rem',
              }}
            >
              {`${formData.firstName} ${formData.lastName}` || 'Nome não definido'}
            </Typography>
            <Typography
              sx={{
                color: theme.colors.text.secondary,
                fontSize: '0.875rem',
              }}
            >
              {user?.email}
            </Typography>
          </CardContent>
        </Card>

        {/* Form */}
        <Card sx={{
          marginBottom: 3,
          borderRadius: theme.radii.medium,
          backgroundColor: theme.colors.card,
          border: 'none',
          boxShadow: 'none',
        }}>
          <CardContent sx={{
            padding: 3,
            '&:last-child': { paddingBottom: 3 }
          }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                label="Nome"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                disabled={!editing}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: editing ? theme.colors.background : 'transparent',
                  },
                }}
              />
              <TextField
                fullWidth
                label="Sobrenome"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                disabled={!editing}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: editing ? theme.colors.background : 'transparent',
                  },
                }}
              />
            </Box>

            <TextField
              fullWidth
              label="@ Apelido (HostlyClub)"
              value={formData.nickname}
              onChange={(e) => handleInputChange('nickname', e.target.value.replace(/[^a-zA-Z0-9._]/g, '').toLowerCase())}
              disabled={!editing}
              size="small"
              placeholder="seu.apelido"
              helperText={editing ? 'Apenas letras, numeros, pontos e underline' : ''}
              slotProps={{
                input: {
                  startAdornment: <Typography sx={{ color: theme.colors.text.secondary, mr: 0.5, fontSize: '0.875rem' }}>@</Typography>,
                }
              }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: editing ? theme.colors.background : 'transparent',
                },
              }}
            />

            <TextField
              fullWidth
              label="Telefone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              disabled={!editing}
              size="small"
              placeholder="(81) 99567-3334"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: editing ? theme.colors.background : 'transparent',
                },
              }}
            />

            {/* Campo CEP */}
            <TextField
              fullWidth
              label="CEP"
              value={formData.cep}
              onChange={(e) => handleCepChange(e.target.value)}
              disabled={!editing || loadingCep}
              size="small"
              placeholder="12345-678"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: editing ? theme.colors.background : 'transparent',
                },
              }}
              slotProps={{
                input: {
                  endAdornment: loadingCep ? <CircularProgress size={20} /> : null,
                },
                htmlInput: {
                  maxLength: 9,
                }
              }}
            />

            {/* Endereço (Rua) */}
            <TextField
              fullWidth
              label="Rua"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              disabled={!editing}
              size="small"
              placeholder="Nome da rua"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: editing ? theme.colors.background : 'transparent',
                },
              }}
            />

            {/* Número e Complemento */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Número"
                value={formData.number}
                onChange={(e) => handleInputChange('number', e.target.value)}
                disabled={!editing}
                size="small"
                placeholder="123"
                sx={{
                  width: '30%',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: editing ? theme.colors.background : 'transparent',
                  },
                }}
              />
              <TextField
                fullWidth
                label="Complemento"
                value={formData.complement}
                onChange={(e) => handleInputChange('complement', e.target.value)}
                disabled={!editing}
                size="small"
                placeholder="Apto 101, Bloco A"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: editing ? theme.colors.background : 'transparent',
                  },
                }}
              />
            </Box>

            {/* Bairro */}
            <TextField
              fullWidth
              label="Bairro"
              value={formData.neighborhood}
              onChange={(e) => handleInputChange('neighborhood', e.target.value)}
              disabled={!editing}
              size="small"
              placeholder="Nome do bairro"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: editing ? theme.colors.background : 'transparent',
                },
              }}
            />

            {/* Cidade e Estado */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                label="Cidade"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                disabled={!editing}
                size="small"
                placeholder="Nome da cidade"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: editing ? theme.colors.background : 'transparent',
                  },
                }}
              />
              <TextField
                label="Estado"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                disabled={!editing}
                size="small"
                placeholder="UF"
                sx={{
                  width: '30%',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: editing ? theme.colors.background : 'transparent',
                  },
                }}
                slotProps={{
                  htmlInput: {
                    maxLength: 2,
                  }
                }}
              />
            </Box>

            {/* Campo Pinterest */}
            <TextField
              fullWidth
              label="Link do Pinterest (Mural de Inspirações)"
              value={formData.pinterestBoardUrl}
              onChange={(e) => handleInputChange('pinterestBoardUrl', e.target.value)}
              disabled={!editing}
              size="small"
              placeholder="https://br.pinterest.com/seu-usuario/seu-board/"
              slotProps={{
                input: {
                  startAdornment: <Pinterest sx={{ mr: 1, color: theme.colors.text.secondary }} />,
                }
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: editing ? theme.colors.background : 'transparent',
                },
              }}
            />

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {editing ? (
                <>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading || !user?.uid}
                    startIcon={loading ? <CircularProgress size={16} /> : <Save />}
                    sx={{
                      backgroundColor: theme.colors.primary,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.colors.primary,
                        opacity: 0.8,
                      },
                    }}
                  >
                    {loading ? 'Salvando...' : 'Salvar'}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={loading}
                    startIcon={<Cancel />}
                    sx={{
                      borderColor: theme.colors.border,
                      color: theme.colors.text.primary,
                      '&:hover': {
                        borderColor: theme.colors.primary,
                      },
                    }}
                  >
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setEditing(true)}
                  disabled={!user?.uid}
                  startIcon={<Edit />}
                  sx={{
                    backgroundColor: theme.colors.secondary,
                    color: theme.colors.text.onSecondary,
                    '&:hover': {
                      backgroundColor: theme.colors.secondary,
                      opacity: 0.8,
                    },
                  }}
                >
                  Editar Perfil
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Subscription Section */}
        <Card sx={{
          marginBottom: 3,
          borderRadius: theme.radii.medium,
          backgroundColor: theme.colors.card,
          border: 'none',
          boxShadow: 'none',
        }}>
          <CardContent sx={{
            padding: 3,
            '&:last-child': { paddingBottom: 3 }
          }}>
            <Typography
              sx={{
                color: theme.colors.text.primary,
                fontWeight: 'bold',
                fontSize: '0.875rem',
                marginBottom: 2,
              }}
            >
              ASSINATURA
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>
                  Plano Atual
                </Typography>
                <Chip
                  label={getPlanLabel()}
                  sx={{
                    backgroundColor: getPlanColor(),
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                  }}
                />
              </Box>

              {subscription?.currentPeriodEnd && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>
                    Renovação
                  </Typography>
                  <Typography sx={{ color: theme.colors.text.primary, fontSize: '0.875rem', fontWeight: 'bold' }}>
                    {formatDate(subscription.currentPeriodEnd)}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>
                  Status
                </Typography>
                <Chip
                  label={subscription?.status === 'active' ? 'Ativa' : subscription?.status === 'trialing' ? 'Em Teste' : 'Inativa'}
                  size="small"
                  color={subscription?.status === 'active' || subscription?.status === 'trialing' ? 'success' : 'default'}
                />
              </Box>
            </Box>

            {!isFree && subscription?.stripeCustomerId && (
              <>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  sx={{
                    borderColor: theme.colors.border,
                    color: theme.colors.text.primary,
                    justifyContent: 'center',
                    mb: 1,
                    '&:hover': {
                      borderColor: theme.colors.primary,
                      backgroundColor: `${theme.colors.primary}10`,
                    },
                  }}
                >
                  {portalLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    'Atualizar Método de Pagamento'
                  )}
                </Button>

                {subscription?.cancelAtPeriodEnd ? (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Sua assinatura será cancelada em {formatDate(subscription.currentPeriodEnd)}. Você manterá acesso até esta data.
                  </Alert>
                ) : (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setCancelDialogOpen(true)}
                    sx={{
                      borderColor: '#EF4444',
                      color: '#EF4444',
                      justifyContent: 'center',
                      mb: 1,
                      '&:hover': {
                        borderColor: '#DC2626',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      },
                    }}
                  >
                    Cancelar Assinatura
                  </Button>
                )}
              </>
            )}

            {Capacitor.getPlatform() === 'ios' && (
              <Button
                fullWidth
                variant="outlined"
                onClick={handleRestorePurchases}
                disabled={restoreLoading}
                sx={{
                  borderColor: theme.colors.border,
                  color: theme.colors.text.primary,
                  justifyContent: 'center',
                  mb: 1,
                  '&:hover': {
                    borderColor: theme.colors.primary,
                    backgroundColor: `${theme.colors.primary}10`,
                  },
                }}
              >
                {restoreLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  'Restaurar Compras'
                )}
              </Button>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={async () => {
                  setLoading(true);
                  try {
                    await refreshSubscription();
                    showSnackbar('Assinatura atualizada!', 'success');
                  } catch (error) {
                    showSnackbar('Erro ao atualizar assinatura', 'error');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                sx={{
                  borderColor: theme.colors.border,
                  color: theme.colors.text.primary,
                  '&:hover': {
                    borderColor: theme.colors.primary,
                    backgroundColor: `${theme.colors.primary}10`,
                  },
                }}
              >
                {loading ? <CircularProgress size={20} /> : 'Atualizar'}
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate('/pricing')}
                sx={{
                  backgroundColor: theme.colors.primary,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.colors.primary,
                    opacity: 0.8,
                  },
                }}
              >
                {isFree ? 'Fazer Upgrade' : 'Ver Outros Planos'}
              </Button>
            </Box>

            <Button
              fullWidth
              variant="text"
              onClick={async () => {
                if (appleOfferCode.isSupported()) {
                  try {
                    await appleOfferCode.present();
                    return;
                  } catch {
                    // fallback para dialog se a tela nativa falhar
                  }
                }
                setCouponDialogOpen(true);
              }}
              sx={{
                mt: 1,
                color: theme.colors.primary,
                textDecoration: 'underline',
                fontSize: '0.8rem',
                '&:hover': {
                  textDecoration: 'underline',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              Resgatar Cupom
            </Button>
          </CardContent>
        </Card>

        {/* Security Section */}
        <Card sx={{
          borderRadius: theme.radii.medium,
          backgroundColor: theme.colors.card,
          border: 'none',
          boxShadow: 'none',
        }}>
          <CardContent sx={{
            padding: 3,
            '&:last-child': { paddingBottom: 3 }
          }}>
            <Typography
              sx={{
                color: theme.colors.text.primary,
                fontWeight: 'bold',
                fontSize: '0.875rem',
                marginBottom: 2,
              }}
            >
              SEGURANÇA
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Button
              fullWidth
              variant="outlined"
              onClick={() => setResetPasswordDialog(true)}
              startIcon={<Lock />}
              sx={{
                borderColor: theme.colors.border,
                color: theme.colors.text.primary,
                justifyContent: 'flex-start',
                mb: 2,
                '&:hover': {
                  borderColor: theme.colors.primary,
                  backgroundColor: `${theme.colors.primary}10`,
                },
              }}
            >
              Redefinir Senha
            </Button>

            <Divider sx={{ mb: 2 }} />

            <Typography
              sx={{
                color: theme.colors.text.primary,
                fontWeight: 'bold',
                fontSize: '0.875rem',
                marginBottom: 2,
              }}
            >
              ZONA DE PERIGO
            </Typography>

            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Esta ação é <strong>irreversível</strong>. Todos os seus dados serão permanentemente deletados.
              </Typography>
            </Alert>

            <Button
              fullWidth
              variant="outlined"
              onClick={() => setDeleteDialogOpen(true)}
              startIcon={<DeleteForever />}
              sx={{
                borderColor: '#EF4444',
                color: '#EF4444',
                justifyContent: 'flex-start',
                '&:hover': {
                  borderColor: '#DC2626',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                },
              }}
            >
              Deletar Conta Permanentemente
            </Button>
          </CardContent>
        </Card>

        {/* Reset Password Dialog */}
        <Dialog
          open={resetPasswordDialog}
          onClose={() => setResetPasswordDialog(false)}
          slotProps={{
            paper: {
              sx: {
                borderRadius: theme.radii.medium,
                backgroundColor: theme.colors.card,
              }
            }
          }}
        >
          <DialogTitle sx={{ color: theme.colors.text.primary }}>
            Redefinir Senha
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: theme.colors.text.primary, mb: 2 }}>
              Um email será enviado para <strong>{user?.email}</strong> com instruções para redefinir sua senha.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setResetPasswordDialog(false)}
              sx={{ color: theme.colors.text.primary }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetPasswordLoading}
              sx={{
                backgroundColor: theme.colors.primary,
                color: 'white',
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              {resetPasswordLoading ? <CircularProgress size={20} /> : 'Enviar Email'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Subscription Dialog */}
        <Dialog
          open={cancelDialogOpen}
          onClose={() => !cancelLoading && setCancelDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                borderRadius: theme.radii.medium,
                backgroundColor: theme.colors.card,
              }
            }
          }}
        >
          <DialogTitle sx={{ color: theme.colors.text.primary, fontWeight: 'bold' }}>
            Cancelar Assinatura
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Como funciona o cancelamento:
              </Typography>
              <Typography variant="body2" component="div">
                • Você manterá acesso completo até <strong>{formatDate(subscription?.currentPeriodEnd)}</strong>
                <br />
                • Não haverá cobrança após o cancelamento
                <br />
                • Você pode reativar a qualquer momento antes do fim do período
                <br />
                • Todos os seus dados permanecerão salvos
              </Typography>
            </Alert>

            <Typography sx={{ color: theme.colors.text.primary, mb: 2 }}>
              Tem certeza que deseja cancelar sua assinatura <strong>{getPlanLabel()}</strong>?
            </Typography>

            {subscription?.currentPeriodEnd && (
              <Box sx={{
                backgroundColor: theme.colors.background,
                p: 2,
                borderRadius: theme.radii.small,
                mb: 2
              }}>
                <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                  Data de renovação atual
                </Typography>
                <Typography variant="body1" sx={{ color: theme.colors.text.primary, fontWeight: 'bold' }}>
                  {formatDate(subscription.currentPeriodEnd)}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.colors.text.secondary, mt: 1, display: 'block' }}>
                  Após esta data, sua assinatura voltará para o plano Free
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelLoading}
              sx={{ color: theme.colors.text.primary }}
            >
              Manter Assinatura
            </Button>
            <Button
              onClick={handleCancelSubscription}
              disabled={cancelLoading}
              variant="contained"
              sx={{
                backgroundColor: '#EF4444',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#DC2626',
                },
                '&:disabled': {
                  backgroundColor: '#EF4444',
                  opacity: 0.6,
                }
              }}
            >
              {cancelLoading ? <CircularProgress size={20} color="inherit" /> : 'Confirmar Cancelamento'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Coupon Redemption Dialog */}
        <Dialog open={couponDialogOpen} onClose={() => setCouponDialogOpen(false)}>
          <DialogTitle>Resgatar Cupom</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Insira seu código abaixo para ativar benefícios exclusivos.
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Código do Cupom"
              fullWidth
              variant="outlined"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              disabled={couponLoading}
            />
            {couponMessage && (
              <Alert severity={couponMessage.type} sx={{ mt: 2 }}>
                {couponMessage.text}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCouponDialogOpen(false)} disabled={couponLoading}>
              Cancelar
            </Button>
            <Button onClick={handleRedeemCoupon} variant="contained" disabled={couponLoading || !couponCode.trim()}>
              {couponLoading ? <CircularProgress size={24} /> : 'Resgatar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Account Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                borderRadius: theme.radii.medium,
                backgroundColor: theme.colors.card,
              }
            }
          }}
        >
          <DialogTitle sx={{
            color: '#EF4444',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
            <Warning /> Deletar Conta Permanentemente
          </DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                ATENÇÃO: Esta ação é IRREVERSÍVEL!
              </Typography>
              <Typography variant="body2" component="div">
                Ao deletar sua conta:
                <br />
                Todos os seus eventos serão deletados
                <br />
                Todos os seus convidados serão deletados
                <br />
                Todas as suas receitas serão deletadas
                <br />
                Todo o seu acervo será deletado
                <br />
                Todas as suas tarefas e listas serão deletadas
                <br />
                Todas as suas fotos e arquivos serão deletados
                <br />
                Sua assinatura será cancelada (se houver)
                <br />
                Sua conta será permanentemente removida
              </Typography>
            </Alert>

            <Typography sx={{ color: theme.colors.text.primary, mb: 2, fontWeight: 'bold' }}>
              Tem certeza absoluta que deseja deletar sua conta?
            </Typography>

            <Typography sx={{ color: theme.colors.text.secondary, mb: 2, fontSize: '0.875rem' }}>
              Para confirmar, digite <strong>DELETAR</strong> no campo abaixo:
            </Typography>

            <TextField
              fullWidth
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Digite DELETAR"
              disabled={deleteLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#EF4444',
                  },
                },
              }}
            />

            {deleteConfirmText && deleteConfirmText.toLowerCase() !== 'deletar' && (
              <Typography sx={{ color: '#EF4444', mt: 1, fontSize: '0.75rem' }}>
                O texto deve ser exatamente "DELETAR"
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeleteConfirmText('');
              }}
              disabled={deleteLoading}
              sx={{ color: theme.colors.text.primary }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteAccount}
              disabled={deleteLoading || deleteConfirmText.toLowerCase() !== 'deletar'}
              variant="contained"
              sx={{
                backgroundColor: '#EF4444',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#DC2626',
                },
                '&:disabled': {
                  backgroundColor: '#EF4444',
                  opacity: 0.6,
                }
              }}
            >
              {deleteLoading ? <CircularProgress size={20} color="inherit" /> : 'Deletar Minha Conta'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};